import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowUpRight,
  Check,
  Brain,
  ChevronDown,
  CircleAlert,
  CircleCheck,
  CircleX,
  Eye,
  EyeOff,
  Globe,
  KeyRound,
  Lock,
  PenLine,
  Plus,
  ScanLine,
  Telescope,
  Trash2,
} from 'lucide-react';
import {
  settingsService,
  AISettings,
  AIModelInfo,
  AIModelSelections,
  AIProviderType,
  AIUseCase,
  AIConnectionCheck,
} from '../../services/settingsService';
import { asOneOf, useUrlState } from '../../hooks/useUrlState';
import { Button, Card, Dot, IconButton, Page, PageHeader, Pill, SectionHeader, Spinner } from '../ui';
import { ModelPickerModal } from './ai/ModelPickerModal';
import { ProviderMark } from './ai/ProviderMark';
import { AI_PROVIDERS, ProviderMeta, providerName, shortKey } from './ai/providers';
import { estimateUseCost, formatCost, USE_CASE_META } from './ai/modelFormat';

/* One codec per parameter, made once: the URL state reads them on every render */
const PROVIDER = asOneOf<AIProviderType | ''>(AI_PROVIDERS.map(provider => provider.id));

interface AIProviderSettingsProps {
  onSettingsChange?: (settings: AISettings) => void;
}

const USE_CASES: AIUseCase[] = ['businessCard', 'writing', 'search', 'research', 'analysis'];

const USE_CASE_ICONS: Record<AIUseCase, React.ComponentType<{ className?: string; strokeWidth?: number }>> = {
  businessCard: ScanLine,
  writing: PenLine,
  search: Globe,
  research: Telescope,
  analysis: Brain,
};

type UseCaseFlags = Record<AIUseCase, boolean>;
type ModelInfoMap = Partial<Record<AIProviderType, Partial<Record<AIUseCase, AIModelInfo | null>>>>;

interface ProviderTestResult {
  success: boolean;
  message: string;
  checks: Array<AIConnectionCheck & { useCase: AIUseCase; model?: string }>;
}

export const AIProviderSettings: React.FC<AIProviderSettingsProps> = ({ onSettingsChange }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [testing, setTesting] = useState<Record<string, boolean>>({});
  const [testResults, setTestResults] = useState<Record<string, ProviderTestResult | undefined>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [apiKeys, setApiKeys] = useState<Record<string, string>>({});
  const [apiKeysMasked, setApiKeysMasked] = useState<Record<string, boolean>>({});
  const [showApiKeys, setShowApiKeys] = useState<Record<string, boolean>>({});
  const [useCases, setUseCases] = useState<Record<string, UseCaseFlags>>({});
  const [models, setModels] = useState<AIModelSelections>({});
  const [defaultModels, setDefaultModels] = useState<AISettings['defaultModels']>({});
  const [modelInfo, setModelInfo] = useState<ModelInfoMap>({});
  // Which key is open is in the address bar; a name it does not know opens none
  const [expanded, setExpanded] = useUrlState<AIProviderType | ''>('provider', '', PROVIDER);
  const [picker, setPicker] = useState<AIUseCase | null>(null);
  const savedTimer = useRef<number | undefined>(undefined);

  const hasKey = (providerId: string) => !!apiKeys[providerId]?.trim();

  const effectiveModel = (providerId: AIProviderType, useCase: AIUseCase): string =>
    models[providerId]?.[useCase] || defaultModels?.[providerId]?.[useCase] || '';

  const assignedProvider = (useCase: AIUseCase): AIProviderType | undefined =>
    (Object.entries(useCases).find(([, flags]) => flags[useCase])?.[0] as AIProviderType | undefined);

  const typedKeyFor = useCallback(
    (providerId: AIProviderType) => (apiKeysMasked[providerId] ? undefined : apiKeys[providerId]),
    [apiKeys, apiKeysMasked],
  );

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const currentSettings = await settingsService.getAISettings();

      const initialApiKeys: Record<string, string> = {};
      const initialApiKeysMasked: Record<string, boolean> = {};
      const initialUseCases: Record<string, UseCaseFlags> = {};

      AI_PROVIDERS.forEach(provider => {
        const isBusinessCardProvider = provider.id === currentSettings.provider;
        const isWritingProvider = provider.id === currentSettings.writingProvider;
        const isResearchProvider = provider.id === currentSettings.researchProvider;
        const isSearchProvider = provider.id === currentSettings.searchProvider;
        const isAnalysisProvider = provider.id === currentSettings.analysisProvider;

        if (currentSettings.apiKeys && currentSettings.apiKeys[provider.id]) {
          initialApiKeys[provider.id] = currentSettings.apiKeys[provider.id];
          initialApiKeysMasked[provider.id] = currentSettings.apiKeysMasked?.[provider.id] || false;
        } else if ((isBusinessCardProvider || isWritingProvider) && currentSettings.apiKey) {
          // Fallback to legacy single API key for backward compatibility
          initialApiKeys[provider.id] = currentSettings.apiKey;
          initialApiKeysMasked[provider.id] = currentSettings.apiKeyMasked || false;
        } else {
          initialApiKeys[provider.id] = '';
          initialApiKeysMasked[provider.id] = false;
        }

        initialUseCases[provider.id] = {
          businessCard: isBusinessCardProvider,
          writing: isWritingProvider,
          search: isSearchProvider,
          research: isResearchProvider,
          analysis: isAnalysisProvider,
        };
      });

      setApiKeys(initialApiKeys);
      setApiKeysMasked(initialApiKeysMasked);
      setUseCases(initialUseCases);
      setModels(currentSettings.models || {});
      setDefaultModels(currentSettings.defaultModels || {});
      setModelInfo((currentSettings.modelDetails || {}) as ModelInfoMap);
      setDirty(false);
      onSettingsChange?.(currentSettings);
    } catch (error) {
      console.error('Failed to load AI settings:', error);
      setErrors({ general: 'Failed to load AI settings' });
    } finally {
      setLoading(false);
    }
  }, [onSettingsChange]);

  useEffect(() => {
    loadSettings();
    return () => window.clearTimeout(savedTimer.current);
  }, [loadSettings]);

  const markDirty = () => {
    setDirty(true);
    setJustSaved(false);
  };

  const handleApiKeyChange = (providerId: string, apiKey: string) => {
    setApiKeys(prev => ({ ...prev, [providerId]: apiKey }));
    setApiKeysMasked(prev => ({ ...prev, [providerId]: false }));
    setTestResults(prev => ({ ...prev, [providerId]: undefined }));
    markDirty();

    // Clearing the key turns the provider off for every use case
    if (apiKey.trim() === '' && USE_CASES.some(useCase => useCases[providerId]?.[useCase])) {
      setUseCases(prev => ({
        ...prev,
        [providerId]: {
          businessCard: false,
          writing: false,
          search: false,
          research: false,
          analysis: false,
        },
      }));
    }
  };

  const handleModelSelected = (providerId: AIProviderType, modelId: string | null, info: AIModelInfo | null) => {
    if (!picker) return;
    const useCase = picker;

    // Only one provider can serve each use case
    setUseCases(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(id => {
        next[id] = { ...next[id], [useCase]: id === providerId };
      });
      return next;
    });
    setModels(prev => {
      const forProvider = { ...(prev[providerId] || {}) };
      if (modelId) forProvider[useCase] = modelId;
      else delete forProvider[useCase];
      return { ...prev, [providerId]: forProvider };
    });
    setModelInfo(prev => ({
      ...prev,
      [providerId]: { ...(prev[providerId] || {}), [useCase]: info },
    }));
    setTestResults(prev => ({ ...prev, [providerId]: undefined }));
    setErrors(prev => ({ ...prev, general: '' }));
    markDirty();
    setPicker(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setErrors({});

      const businessCardProvider = assignedProvider('businessCard');
      const writingProvider = assignedProvider('writing');
      const researchProvider = assignedProvider('research');
      const searchProvider = assignedProvider('search');
      const analysisProvider = assignedProvider('analysis');

      if (businessCardProvider) {
        const info = modelInfo[businessCardProvider]?.businessCard;
        if (info?.capabilities.imageInput === false) {
          setErrors({ general: `${info.name} can't read images. Choose a vision model for business cards.` });
          return;
        }
      }

      const selectedProvider = businessCardProvider || writingProvider;
      if (selectedProvider && !hasKey(selectedProvider)) {
        setErrors({ general: `Add an API key for ${providerName(selectedProvider)}` });
        return;
      }

      const settingsToSave: any = {
        provider: businessCardProvider || 'claude', // Default to claude if no business card provider
        apiKeys: {},
        models,
      };
      if (writingProvider) settingsToSave.writingProvider = writingProvider;
      if (researchProvider) settingsToSave.researchProvider = researchProvider;
      if (searchProvider) settingsToSave.searchProvider = searchProvider;
      if (analysisProvider) settingsToSave.analysisProvider = analysisProvider;

      // Send every key (masked ones are ignored server-side; empty ones are removed)
      Object.entries(apiKeys).forEach(([providerId, apiKey]) => {
        settingsToSave.apiKeys[providerId] = apiKey || '';
      });

      const updatedSettings = await settingsService.updateAISettings(settingsToSave);
      setModels(updatedSettings.models || {});
      if (updatedSettings.apiKeys) {
        setApiKeys(prev => ({ ...prev, ...updatedSettings.apiKeys }));
        setApiKeysMasked(prev => ({ ...prev, ...updatedSettings.apiKeysMasked }));
        setShowApiKeys({});
      }
      if (updatedSettings.modelDetails) {
        // Keep live details picked in this session where the catalog has none
        setModelInfo(prev => {
          const merged: ModelInfoMap = { ...prev };
          Object.entries(updatedSettings.modelDetails || {}).forEach(([providerId, byUseCase]) => {
            const id = providerId as AIProviderType;
            merged[id] = { ...(prev[id] || {}) };
            Object.entries(byUseCase || {}).forEach(([useCase, info]) => {
              if (info) merged[id]![useCase as AIUseCase] = info;
            });
          });
          return merged;
        });
      }
      setDirty(false);
      setJustSaved(true);
      window.clearTimeout(savedTimer.current);
      savedTimer.current = window.setTimeout(() => setJustSaved(false), 2500);
      onSettingsChange?.(updatedSettings);
    } catch (error: any) {
      console.error('Failed to save AI settings:', error);
      setErrors({ general: error?.response?.data?.message || 'Failed to save AI settings' });
    } finally {
      setSaving(false);
    }
  };

  /** Tests every use case the provider serves with its chosen model (text only when it serves none). */
  const handleTestConnection = async (provider: ProviderMeta) => {
    const providerId = provider.id;
    if (!hasKey(providerId)) return;

    setTesting(prev => ({ ...prev, [providerId]: true }));
    setTestResults(prev => ({ ...prev, [providerId]: undefined }));
    const enabled = USE_CASES.filter(useCase => useCases[providerId]?.[useCase]);
    const toTest: AIUseCase[] = enabled.length ? enabled : ['writing'];

    const checks: ProviderTestResult['checks'] = [];
    const failures: string[] = [];
    const testedTextModels = new Set<string>();
    let outcome: ProviderTestResult;
    try {
      for (const useCase of toTest) {
        const model = effectiveModel(providerId, useCase);
        const result = await settingsService.testAIConnection(providerId, {
          model: model || undefined,
          useCase,
          apiKey: typedKeyFor(providerId),
        });
        const resultChecks = result.checks ?? [];
        if (!resultChecks.length && !result.success) failures.push(result.message);
        resultChecks.forEach(check => {
          // Text checks repeat for use cases sharing a model; show each model once
          if (check.id === 'text') {
            if (testedTextModels.has(`${result.model}`)) return;
            testedTextModels.add(`${result.model}`);
          }
          checks.push({ ...check, useCase, model: result.model });
          if (!check.success) failures.push(check.message);
        });
      }
      outcome = { success: failures.length === 0, message: failures[0] ?? 'Ready', checks };
    } catch (error: any) {
      console.error('Failed to test AI connection:', error);
      outcome = {
        success: false,
        message: error?.response?.data?.message || 'Connection test failed',
        checks,
      };
    }
    setTestResults(prev => ({ ...prev, [providerId]: outcome }));
    if (!outcome.success) setExpanded(providerId);
    setTesting(prev => ({ ...prev, [providerId]: false }));
  };

  const connected = AI_PROVIDERS.filter(p => hasKey(p.id));
  const orderedProviders = [...connected, ...AI_PROVIDERS.filter(p => !hasKey(p.id))];

  return (
    <Page>
      <PageHeader
        title="AI settings"
        actions={
        <div className="flex items-center gap-3">
          {dirty && (
            <span className="inline-flex items-center gap-2 text-[13px] text-neutral-500">
              <Dot tone="red" /> Unsaved
            </span>
          )}
          {justSaved && !dirty && (
            <span className="inline-flex animate-fade-up items-center gap-1.5 text-[13px] text-neutral-500">
              <Check className="h-3.5 w-3.5 text-emerald-600" strokeWidth={2} /> Saved
            </span>
          )}
          <Button variant="primary" onClick={handleSave} loading={saving} disabled={!dirty || loading}>
            Save
          </Button>
        </div>
        }
      />

      {errors.general && (
        <p role="alert" className="mb-5 flex animate-fade-up items-center gap-2 rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">
          <CircleAlert className="h-4 w-4 flex-shrink-0" strokeWidth={1.75} />
          {errors.general}
        </p>
      )}

      {/* Use cases */}
      <SectionHeader title="Use cases" />
      <div className="grid grid-cols-1 divide-y divide-neutral-200/70 overflow-hidden rounded-2xl border border-neutral-200 bg-white md:grid-cols-3 md:divide-x md:divide-y-0">
        {USE_CASES.map(useCase => {
          if (loading) {
            return (
              <div key={useCase} className="h-[132px] p-4">
                <div className="h-4 w-24 animate-pulse rounded-full bg-neutral-100" />
                <div className="mt-6 h-5 w-36 animate-pulse rounded-full bg-neutral-100" />
                <div className="mt-4 h-4 w-28 animate-pulse rounded-full bg-neutral-100" />
              </div>
            );
          }
          const meta = USE_CASE_META[useCase];
          const Icon = USE_CASE_ICONS[useCase];
          const providerId = assignedProvider(useCase);
          const modelId = providerId ? effectiveModel(providerId, useCase) : '';
          const info = providerId ? modelInfo[providerId]?.[useCase] ?? null : null;
          const cost = info ? estimateUseCost(info, useCase) : null;
          const cannotReadImages = meta.needsImage && info?.capabilities.imageInput === false;
          const missingKey = !!providerId && !hasKey(providerId);
          const disabled = connected.length === 0;

          return (
            <button
              key={useCase}
              type="button"
              disabled={disabled}
              onClick={() => setPicker(useCase)}
              aria-label={`${meta.shortLabel}: ${providerId ? `${info?.name || modelId} on ${providerName(providerId)}` : 'not set'}. Change model`}
              className="group flex min-h-[120px] flex-col p-4 text-left md:min-h-[132px] transition-colors hover:bg-neutral-50/80 focus-visible:bg-neutral-50 focus-visible:outline-none disabled:cursor-not-allowed disabled:hover:bg-white"
            >
              <span className="flex w-full items-center justify-between text-[13px] text-neutral-500">
                <span className="inline-flex items-center gap-2">
                  <Icon className="h-4 w-4" strokeWidth={1.75} />
                  {meta.shortLabel}
                </span>
                <ArrowUpRight
                  className="h-4 w-4 text-neutral-300 transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-ink"
                  strokeWidth={1.75}
                />
              </span>

              <span
                className={`mt-auto line-clamp-2 w-full break-words pt-5 text-[20px] leading-[1.2] tracking-[-0.02em] ${
                  providerId ? 'text-ink' : 'text-neutral-300'
                }`}
                title={modelId}
              >
                {providerId ? info?.name || modelId || 'Default model' : disabled ? 'Add a key' : 'Not set'}
              </span>

              <span className="mt-2.5 flex w-full items-center justify-between gap-2 text-[12px] text-neutral-500">
                {providerId ? (
                  <span className="inline-flex min-w-0 items-center gap-2">
                    <ProviderMark name={providerName(providerId)} size="sm" />
                    <span className="truncate">{providerName(providerId)}</span>
                  </span>
                ) : (
                  <span />
                )}
                {cannotReadImages ? (
                  <Pill tone="red">No vision</Pill>
                ) : missingKey ? (
                  <Pill tone="red">No key</Pill>
                ) : cost !== null ? (
                  <Pill tone="neutral" title={`Estimated per ${meta.estimate.unit}`}>
                    {formatCost(cost)} / {meta.estimate.unit}
                  </Pill>
                ) : null}
              </span>
            </button>
          );
        })}
      </div>

      {/* Providers */}
      <SectionHeader
        title="Providers"
        className="mt-8"
        aside={!loading && `${connected.length} of ${AI_PROVIDERS.length} connected`}
      />
      <Card divided>
        {loading
          ? Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3">
                <div className="h-8 w-8 animate-pulse rounded-full bg-neutral-100" />
                <div className="h-4 w-32 animate-pulse rounded-full bg-neutral-100" />
              </div>
            ))
          : orderedProviders.map(provider => (
              <ProviderRow
                key={provider.id}
                provider={provider}
                apiKey={apiKeys[provider.id] || ''}
                masked={!!apiKeysMasked[provider.id]}
                revealed={!!showApiKeys[provider.id]}
                usedFor={USE_CASES.filter(useCase => useCases[provider.id]?.[useCase])}
                expanded={expanded === provider.id}
                testing={!!testing[provider.id]}
                result={testResults[provider.id]}
                onToggle={() => setExpanded(expanded === provider.id ? '' : provider.id)}
                onKeyChange={value => handleApiKeyChange(provider.id, value)}
                onReveal={() => setShowApiKeys(prev => ({ ...prev, [provider.id]: !prev[provider.id] }))}
                onTest={() => handleTestConnection(provider)}
              />
            ))}
      </Card>
      <p className="mt-3 flex items-center gap-1.5 text-[12px] text-neutral-400">
        <Lock className="h-3 w-3" strokeWidth={1.75} /> Keys are encrypted and visible to admins only
      </p>

      {picker && (
        <ModelPickerModal
          open
          useCase={picker}
          providers={connected}
          initialProvider={
            (assignedProvider(picker) && hasKey(assignedProvider(picker)!) ? assignedProvider(picker) : connected[0]?.id) ||
            'claude'
          }
          assignedProvider={assignedProvider(picker)}
          modelFor={providerId => effectiveModel(providerId, picker)}
          defaultModelFor={providerId => defaultModels?.[providerId]?.[picker]}
          typedKeyFor={typedKeyFor}
          onSelect={handleModelSelected}
          onClose={() => setPicker(null)}
        />
      )}
    </Page>
  );
};

interface ProviderRowProps {
  provider: ProviderMeta;
  apiKey: string;
  masked: boolean;
  revealed: boolean;
  usedFor: AIUseCase[];
  expanded: boolean;
  testing: boolean;
  result?: ProviderTestResult;
  onToggle: () => void;
  onKeyChange: (value: string) => void;
  onReveal: () => void;
  onTest: () => void;
}

const ProviderRow: React.FC<ProviderRowProps> = ({
  provider,
  apiKey,
  masked,
  revealed,
  usedFor,
  expanded,
  testing,
  result,
  onToggle,
  onKeyChange,
  onReveal,
  onTest,
}) => {
  const keyPresent = !!apiKey.trim();
  const panelId = `provider-panel-${provider.id}`;

  return (
    <div className={expanded ? 'bg-neutral-50/50' : ''}>
      <div className="flex items-center gap-3 px-4 py-2.5">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-controls={panelId}
          aria-label={`${provider.name}, ${keyPresent ? 'key added' : 'no key'}`}
          className="flex min-w-0 flex-1 items-center gap-3 rounded-xl text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
        >
          <ProviderMark name={provider.name} muted={!keyPresent} />
          <span className="min-w-0 flex-1">
            <span className={`block text-[14px] ${keyPresent ? 'text-ink' : 'text-neutral-500'}`}>{provider.name}</span>
            <span className="block truncate font-ui-mono text-[11.5px] text-neutral-400">
              {keyPresent ? shortKey(apiKey) : 'No key'}
            </span>
          </span>
          <span className="hidden flex-wrap justify-end gap-1.5 md:flex">
            {usedFor.map(useCase => (
              <Pill key={useCase} tone="outline">
                {USE_CASE_META[useCase].shortLabel}
              </Pill>
            ))}
          </span>
        </button>

        <TestStatus testing={testing} result={result} />

        {keyPresent ? (
          <Button size="sm" variant="secondary" onClick={onTest} disabled={testing}>
            Test
          </Button>
        ) : (
          !expanded && (
            <Button size="sm" variant="ghost" onClick={onToggle} className="text-neutral-500">
              <Plus className="h-3.5 w-3.5" strokeWidth={2} />
              Add key
            </Button>
          )
        )}
        <IconButton label={expanded ? 'Collapse' : 'Edit key'} size="sm" onClick={onToggle}>
          <ChevronDown
            className={`h-4 w-4 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
            strokeWidth={1.75}
          />
        </IconButton>
      </div>

      {expanded && (
        <div id={panelId} className="animate-fade-up px-4 pb-4 sm:pl-[60px]">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[220px] flex-1">
              <KeyRound
                className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400"
                strokeWidth={1.75}
              />
              <input
                autoFocus={!keyPresent}
                type={revealed && !masked ? 'text' : 'password'}
                value={apiKey}
                onChange={e => onKeyChange(e.target.value)}
                onFocus={e => masked && e.target.select()}
                placeholder={provider.keyPlaceholder}
                aria-label={`${provider.name} API key`}
                autoComplete="off"
                spellCheck={false}
                className="h-9 w-full rounded-full border-0 bg-white pl-10 pr-11 font-ui-mono text-[16px] sm:text-[13px] text-ink ring-1 ring-inset ring-neutral-200 placeholder:text-neutral-300 focus:ring-2 focus:ring-primary-600/40"
              />
              {!masked && keyPresent && (
                <IconButton
                  label={revealed ? 'Hide key' : 'Show key'}
                  size="sm"
                  onClick={onReveal}
                  className="absolute right-1 top-1/2 -translate-y-1/2"
                >
                  {revealed ? <EyeOff className="h-4 w-4" strokeWidth={1.75} /> : <Eye className="h-4 w-4" strokeWidth={1.75} />}
                </IconButton>
              )}
            </div>
            <a
              href={provider.keyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-9 items-center gap-1 rounded-full px-3 text-[13px] text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-ink"
            >
              Get key <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.75} />
            </a>
            {keyPresent && (
              <IconButton label="Remove key" onClick={() => onKeyChange('')} className="hover:bg-primary-50 hover:text-primary-700">
                <Trash2 className="h-4 w-4" strokeWidth={1.75} />
              </IconButton>
            )}
          </div>
          {masked && <p className="mt-2 pl-4 text-xs text-neutral-400">Type to replace the saved key</p>}

          {result && (
            <ul className="mt-4 space-y-2.5" aria-label="Test results">
              {result.checks.length === 0 && !result.success && (
                <li className="flex items-start gap-2.5 text-[13px] text-primary-700">
                  <CircleX className="mt-px h-4 w-4 flex-shrink-0" strokeWidth={1.75} />
                  <span className="break-words">{result.message}</span>
                </li>
              )}
              {result.checks.map((check, index) => (
                <li key={`${check.useCase}-${check.id}-${index}`} className="text-[13px]">
                  <div className="flex items-center gap-2.5">
                    {check.success ? (
                      <CircleCheck className="h-4 w-4 flex-shrink-0 text-emerald-600" strokeWidth={1.75} />
                    ) : (
                      <CircleX className="h-4 w-4 flex-shrink-0 text-primary-600" strokeWidth={1.75} />
                    )}
                    <span className="text-ink">
                      {check.id === 'text' ? 'Text' : check.id === 'image' ? 'Image' : 'Web search'}
                    </span>
                    {check.model && <span className="truncate font-ui-mono text-[11.5px] text-neutral-400">{check.model}</span>}
                    <span className="ml-auto flex-shrink-0 tabular-nums text-neutral-400">
                      {(check.latencyMs / 1000).toFixed(1)}s
                    </span>
                  </div>
                  {!check.success && <p className="mt-1 break-words pl-[26px] text-xs text-primary-700">{check.message}</p>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

const TestStatus: React.FC<{ testing: boolean; result?: ProviderTestResult }> = ({ testing, result }) => {
  if (testing) {
    return (
      <span className="hidden items-center gap-2 text-[13px] text-neutral-400 sm:inline-flex" role="status">
        <Spinner /> Testing
      </span>
    );
  }
  if (!result) return null;
  return (
    <span
      role="status"
      className={`hidden items-center gap-2 text-[13px] sm:inline-flex ${result.success ? 'text-neutral-500' : 'text-primary-700'}`}
    >
      <Dot tone={result.success ? 'green' : 'red'} />
      {result.success ? 'Ready' : 'Failed'}
    </span>
  );
};
